<template>
  <div class="container-fluid first-view">
    <StudiesUploadProcessBar
      :show-modal="showModal"
      :upload-percent-progress="uploadPercentProgress"
      :upload-progress="currentProgress"
      :error-status="errorStatus"
      :error-message="errorMessage"
      :go-to-details-active="false"
      @close="() => showModal = false"
    />
    <div class="background-overlay"></div>
    <img class="logo" :src="logo" />
    <div class="row w-100 card-overflow">
      <div class="col-12">
        <div class="card mb-0">
          <div class="card-header view">
            <div class="header">
              <div class="title">
                <div>Verify Studies</div>
                <button
                v-if="fileProcessData && fileProcessData.status" class="status ml-2 mt-1"
                :class="getStatusClass(fileProcessData.status)"
                @click="fetchFiles"
              >
                {{ fileProcessData.status.split('.').pop().toLowerCase() }}
              </button>
              <span v-if="lastUpdatedAgo" class="last-updated ml-2">
                Last updated {{ lastUpdatedAgo }}
              </span>
              </div>
              <div class="steps">
                <el-steps :active="1" finish-status="success">
                  <el-step title="Step 1"></el-step>
                  <el-step title="Step 2"></el-step>
                </el-steps>
              </div>
            </div>
            <div v-if="failureMessage" class="failure-message ml-4">
              {{ failureMessage }}
            </div>
          </div>
          <div class="row w-100 p-4" v-if="loadingData">
            <div class="loader" />
            <div class="w-100 text-center font-xl">Loading data</div>
          </div>
          <div class="card-body" v-else>
            <div v-if="studySummaries.length" class="study-summary-panel">
              <h4>Scan Summary</h4>
              <div
                v-for="s in studySummaries"
                :key="s.sid"
                class="study-summary-row"
              >
                <div class="study-summary-row-main">
                  <strong>{{ s.project_name || s.study_path }}</strong>
                  <span class="summary-pill" :class="{ 'summary-pill-warning': s.resolution }">
                    {{ s.question_count }} question{{ s.question_count === 1 ? '' : 's' }}
                  </span>
                  <span v-if="!s.request_form_parsed" class="summary-pill summary-pill-warning">
                    Request form not parsed
                  </span>
                </div>
                <div v-if="s.resolution" class="summary-resolution">
                  &#9888; {{ s.resolution }}
                </div>
              </div>
              <hr/>
            </div>
            <div v-if="studyFiles">
              <h4>Found Studies</h4>
              <hr/>
              <Folder
                v-if="studyFiles.type === 'directory'"
                :structure="studyFiles"
                :title="studyFiles.name || 'Study group'"
                :depth="0"
                :show-files-version-control-button="false"
                :use-customs="true"
                @custom-download="downloadFiles"
                @custom-cancel-upload="customCancelUpload"
                @custom-upload="updateFileProcessFile"
                @custom-on-change="handleChange"
                @set-old-file="setOldFile"
              >
              </Folder>
              <FileList
                v-else
                :file="studyFiles"
                :depth="0"
                :show-version-control-button="false"
                :use-customs="true"
                @custom-download="downloadFiles"
                @custom-cancel-upload="customCancelUpload"
                @custom-upload="updateFileProcessFile"
                @custom-on-change="handleChange"
                @set-old-file="setOldFile"
              >
              </FileList>
            </div>
            <p v-else> No files</p>
          </div>
          <div class="card-footer">
            <div class="d-flex justify-content-end">
              <button
                  class="btn prev-button footer-button"
                  @click="() => $router.push({ name: 'studies-upload' })"
              >
                Back
              </button>
              <button
                  class="btn next-button footer-button"
                  @click="processStudies"
                  :disabled="!fileProcessData.has_uploaded_new_file"
              >
                Process Studies
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import logo from '@/assets/images/logos/Iris-Grey.svg'
import FileList from '../FileStructure/FileList'
import Folder from '../FileStructure/Folder'
import StudiesUploadProcessBar from './StudiesUploadProcessBar'
import studyActions from '@/store/modules/studies/actionTypes'
import Task from '@/helpers/task'

export default {
  name: 'StudiesUploadSecondStep',
  components: { FileList, Folder, StudiesUploadProcessBar },
  data () {
    return {
      logo,
      showModal: false,
      errorStatus: false,
      errorMessage: '',
      loadingData: true,
      fileProcessData: {},
      fileProcessDataErrors: [],
      fileProcessId: this.$route.params.id || '',
      uploadPercentProgress: 0,
      oldFile: {},
      fileList: [],
      studyFiles: {},
      fileProcessStatusClass: {
        'UPLOADED': 'status-uploaded',
        'UNPACKED': 'status-unpacked',
        'VALIDATED': 'status-validated',
        'INJECTED': 'status-injected',
        'ERRORED': 'status-error',
        'SUCCEED': 'status-succeed'
      },
      uploadProgress: {
        UPLOADING: { text: 'Uploading data on the server', status: 'upload' },
        VALIDATION: { text: 'Validation and data injecting', status: 'validation' },
        DONE: { text: 'Done', status: 'done' },
        FAILED: { text: 'Failed', status: 'failed' }
      },
      currentProgress: {},
      uploadStatus: {
        NORMAL: { background: '#3B86FF', icon: 'cloud-upload-alt', text: '' },
        SUCCESS: { background: '#379947', icon: 'cloud', text: 'Uploaded' },
        WARNING: { background: '#DA9251', icon: 'cloud', text: 'Selected' },
        FAILED: { background: '#BB121A', icon: 'cloud', text: 'Failed' },
        UPLOADING: { background: '#DA9251', icon: 'circle-notch', text: 'Uploading' }
      },
      currentStatus: {}
    }
  },
  computed: {
    studySummaries () {
      const meta = this.fileProcessData && this.fileProcessData.tasks_meta_data
      return (meta && meta.summary) || []
    },
    failureMessage () {
      const meta = this.fileProcessData && this.fileProcessData.tasks_meta_data
      const processMeta = meta && meta.process
      return (processMeta && processMeta.message) || ''
    },
    lastUpdatedAgo () {
      const meta = this.fileProcessData && this.fileProcessData.tasks_meta_data
      const processMeta = meta && meta.process
      const at = processMeta && processMeta.at
      if (!at) return ''
      const diffMs = Date.now() - new Date(at).getTime()
      if (isNaN(diffMs) || diffMs < 0) return ''
      const minutes = Math.floor(diffMs / 60000)
      if (minutes < 1) return 'just now'
      if (minutes === 1) return '1 minute ago'
      if (minutes < 60) return `${minutes} minutes ago`
      const hours = Math.floor(minutes / 60)
      return hours === 1 ? '1 hour ago' : `${hours} hours ago`
    }
  },
  methods: {
    ...mapActions({
      getFileProcessDetails: studyActions.FETCH_PROCESS_FILE_DETAILS,
      updateFileProcess: studyActions.UPDATE_FILE_PROCESS,
      downloadFileProcessFile: studyActions.DOWNLOAD_FILE_FROM_FILE_PROCESS,
      processNewStudiesFiles: studyActions.PROCESS_STUDIES
    }),
    changeUploadProcess (progress) {
      switch (progress) {
        case 'uploading':
          this.currentProgress = this.uploadProgress.UPLOADING
          break
        case 'validation':
          this.currentProgress = this.uploadProgress.VALIDATION
          break
        case 'done':
          this.currentProgress = this.uploadProgress.DONE
          break
        case 'failed':
          this.currentProgress = this.uploadProgress.FAILED
          break
        default:
          this.currentProgress = this.uploadProgress.UPLOADING
      }
    },
    setCurrentStatus (status) {
      switch (status) {
        case 'queued':
          this.currentStatus = this.uploadStatus.NORMAL
          break
        case 'selected':
          this.currentStatus = this.uploadStatus.WARNING
          break
        case 'started':
          this.currentStatus = this.uploadStatus.UPLOADING
          break
        case 'failed':
          this.currentStatus = this.uploadStatus.FAILED
          break
        case 'success':
          this.currentStatus = this.uploadStatus.SUCCESS
          break
        default:
          this.currentStatus = this.uploadStatus.NORMAL
      }
    },
    customCancelUpload () {
      this.fileList = []
    },
    handleChange (file) {
      switch (file.status) {
        case 'ready':
          this.fileUpload = {
            filename: file.name,
            file: file,
            status: 'selected'
          }
          this.fileList = [file.raw]
          this.setCurrentStatus(this.fileUpload.status)
          break
        case 'fail':
          this.fileUpload = {
            filename: file.name,
            file: file,
            status: 'failed'
          }
          this.setCurrentStatus(this.fileUpload.status)
          break
      }
    },
    setOldFile (f) {
      this.oldFile = f
    },
    getStatusClass (fileStatus) {
      if (fileStatus) {
        const status = fileStatus.split('.').pop()
        return this.fileProcessStatusClass[status]
      }
    },
    setUploadPercentProgress (value) {
      this.uploadPercentProgress = value
    },
    getPathWithoutFilename (path) {
      let splittedPath = path.split('/')
      const fileName = splittedPath.pop()
      splittedPath = splittedPath.splice(fileName)
      return splittedPath.join('/')
    },
    findErrorsForFile (obj) {
      if (!obj) return
      if (obj.type === 'directory') {
        let errors = this.fileProcessDataErrors.filter(i => i.path === obj.file_path)
        if (errors.length) obj.errors = errors
        else {
          errors = this.fileProcessDataErrors.filter(i => i.path.includes(obj.file_path))
          if (!errors.length) {
            errors = this.fileProcessDataErrors.filter(i => i.request_form && (
              this.getPathWithoutFilename(i.request_form) === obj.file_path
            ))
            if (!errors.length) {
              errors = this.fileProcessDataErrors.filter(i => i.results_file && (
                this.getPathWithoutFilename(i.results_file) === obj.file_path
              ))
            }
          }
          if (errors.length) {
            obj.errors = errors
            obj.hideDetails = true
          }
        }
      } else {
        let errors = this.fileProcessDataErrors.filter(i => i.request_form && (i.request_form === obj.file_path))
        if (!errors.length) errors = this.fileProcessDataErrors.filter(i => i.results_file && (i.results_file === obj.file_path))
        if (errors.length) obj.errors = errors
      }
    },
    setErrorsForObject (object) {
      if (!object) return
      this.findErrorsForFile(object)
      if (object.children) {
        for (const child of object.children) {
          this.setErrorsForObject(child)
        }
      }
    },
    async fetchFiles () {
      if (!this.fileList.length) await this.fetchFileProcessFiles()
    },
    async updateFileProcessFile () {
      this.uploadPercentProgress = 0
      this.changeUploadProcess('uploading')
      this.errorStatus = false
      this.showModal = true
      for (let file of this.fileList) {
        const response = await this.updateFileProcess({
          fileProcessId: this.fileProcessId,
          filePath: this.oldFile.file_path,
          file: file,
          onProgress: this.setUploadPercentProgress
        })
        if (!response) {
          this.errorMessage = 'Failed to send file'
          this.errorStatus = true
          this.changeUploadProcess('failed')
          return
        }
        await this.fetchFileProcessFiles()
        this.changeUploadProcess('done')
      }
    },
    async fetchFileProcessFiles () {
      this.loadingData = true
      if (this.fileProcessId) {
        const processFileDetails = await this.getFileProcessDetails({
          fileProcessId: this.fileProcessId
        })
        this.fileProcessData = (processFileDetails && processFileDetails.file_process) || {}
        this.fileProcessDataErrors = (processFileDetails && processFileDetails.errors) || []
        const fileTree = this.fileProcessData.file_tree
        // file_tree is always a bare list of top-level entries as written by
        // get_files_from_path (backend/pkg/symrise/parser/parse_files.py) --
        // there is no wrapping root node. Wrap it here so <Folder> (which
        // expects a single {type: 'directory', children} structure) renders
        // it, instead of falling through to <FileList> with an array where
        // it expects one file object.
        this.studyFiles = Array.isArray(fileTree)
          ? (fileTree.length ? { type: 'directory', name: 'Study group', file_path: '', children: fileTree } : null)
          : (fileTree || null)
        if (this.studyFiles) {
          this.setErrorsForObject(this.studyFiles)
          this.studyFiles.errors = this.fileProcessDataErrors
          this.studyFiles.hideDetails = true
        }
      }
      this.loadingData = false
    },
    async processStudies () {
      this.uploadPercentProgress = 0
      this.changeUploadProcess('uploading')
      this.cancelUpload = this.errorStatus = false
      this.showModal = true
      const response = await this.processNewStudiesFiles({
        fileProcessId: this.fileProcessId,
        onProgress: this.setUploadPercentProgress
      })
      if (!response) {
        this.errorStatus = true
        this.errorMessage = 'Failed to process studies'
        this.changeUploadProcess('failed')
        return
      }
      const taskId = response['task_id']
      const url = `/fileprocess/${taskId}/status`
      this.changeUploadProcess('validation')
      const task = new Task(url)
      task.on('result', (result) => {
        this.changeUploadProcess('done')
        this.fetchFileProcessFiles()
      })
      task.on('error', (error) => {
        this.errorMessage = error.message ? error.message : error
        this.changeUploadProcess('failed')
        this.errorStatus = true
        this.fetchFileProcessFiles()
      })
    },
    async downloadFiles (f) {
      const data = await this.downloadFileProcessFile({
        processId: this.fileProcessId,
        filePath: f.file_path
      })
      const url = window.URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', f.name)
      document.body.appendChild(link)
      link.click()
    }
  },
  async mounted () {
    this.fileProcessId = this.$route.params.id
    this.setCurrentStatus('queued')
    this.changeUploadProcess('uploading')
    await this.fetchFileProcessFiles()
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables';

.status {
  border: none;
  border-radius: .3rem;
  padding: .25rem .75rem;
  text-align: center;
  font-size: 15px;
}

.status-injected {
  color: $soot;
  background-color: $blue-500;
}
.status-succeed {
  color: $white;
  background-color: $green-600;
}
.status-error {
  color: $soot;
  background-color: $orange-400;
}
.status-uploaded {
  color: $white;
  background-color: $celery-400;
}
.status-unpacked {
  color: $white;
  background-color: $magenta-400;
}
.status-validated {
  color: $white;
  background-color: $fuchsia-400;
}

.failure-message {
  width: 100%;
  color: $orange-400;
  font-size: 14px;
  font-family: 'Source Sans Pro', sans-serif;
}

.study-summary-panel {
  margin-bottom: 1rem;

  h4 {
    margin-bottom: 0.5rem;
  }
}

.study-summary-row {
  padding: 0.5rem 0;
}

.study-summary-row-main {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 15px;
}

.summary-pill {
  display: inline-block;
  padding: 0.15rem 0.6rem;
  border-radius: 1rem;
  font-size: 12px;
  background-color: $blue-500;
  color: $soot;
}

.summary-pill-warning {
  background-color: $orange-400;
  color: $soot;
}

.summary-resolution {
  margin-top: 0.35rem;
  color: $orange-400;
  font-size: 13px;
  font-family: 'Source Sans Pro', sans-serif;
}

.last-updated {
  color: $pewter;
  font-size: 13px;
  font-family: 'Source Sans Pro', sans-serif;
}

.footer-button {
  font-family: 'Open Sans', sans-serif;
  font-weight: 900;
  font-size: 16px;
  line-height: 30px;
  letter-spacing: 0;
  text-transform: uppercase;
  border-radius: 4px;
  width: 160px;
  height: 38px;
  padding: 5px;
  @media screen and (min-width: $break-lg) {
    width: 180px;
    height: 40px;
    padding: 2px;
  }
  @media screen and (min-width: $break-xl) {
    width: 200px;
    height: 42px
  }
  @media screen and (min-width: $break-xxl) {
    width: 240px;
    height: 44px
  }
}
.next-button {
  background-color: $blueberry;
  color: white;
  transition: background-color 0.2s linear;
  &:hover {
    background-color: #9256D9;
  }
}
.prev-button {
  margin-right: .5rem;
  background-color: $charcoal;
  color: white;
  transition: background-color 0.2s linear;
  &:hover {
    background-color: $pewter;
  }
}

.view-wrapper {
  margin-bottom: 0;
  width: 100%;
  background-color: rgba(255, 255, 255, 0.93);
  border: 0;
}

.view {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  padding: 0;
  padding-bottom: 1rem;

  .header {
    display: flex;
    justify-content: space-between;

    .steps {
      width: 270px;
      height: 62px;
      margin-top: 20px;
      margin-right: 29px;
    }
  }

  .form-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    color: $dusty-blue;

    .subtitle {
      @media screen and (min-width: $break-lg) {
        font-size: 25px;
        line-height: 45px;
      }
      @media screen and (min-width: $break-xl) {
        font-size: 30px;
        line-height: 50px;
      }
      @media screen and (min-width: $break-xxl) {
        font-size: 35px;
        line-height: 55px;
      }
      text-align: center;
      font-family: 'Source Sans Pro', sans-serif;
      font-weight: 600;
      font-size: 35px;
      line-height: 55px;
      letter-spacing: 0;
      color: $dusty-blue;
    }

    .form-group {
      @media screen and (min-width: $break-lg) {
        padding: 0 12px;
        margin-top: 5px;
      }
      @media screen and (min-width: $break-xl) {
        padding: 0 32px;
        margin-top: 15px;
      }
      @media screen and (min-width: $break-xxl) {
        padding: 0 98px;
        margin-top: 35px;
      }
      text-align: center;
    }

    .select-group-item {
      @media screen and (min-width: $break-lg) {
        margin: 3px 3px;
        width: 45%;
      }
      @media screen and (min-width: $break-xl) {
        margin: 5px 5px;
        width: 35%;
      }
      @media screen and (min-width: $break-xxl) {
        margin: 5px 10px;
        width: 30%;
      }
    }

    .best-worst-switch {
      margin: 0 30px;
      border-radius: 4px;
      box-shadow: 0 1px 4px #00000026;
    }

    .type-switch {
      margin: 0 15px;
      border-radius: 4px;
      box-shadow: 0 1px 4px #00000026;
    }

    .number-input {
      margin-left: 20px;
      width: 98px !important;
      height: 55px !important;
    }
  }

  .prev-button {
    width: 240px;
    height: 48px;
    font-family: 'Open Sans', sans-serif;
    font-weight: 900;
    font-size: 16px;
    line-height: 30px;
    letter-spacing: 0;
    background: $pewter;
    color: white;
    text-transform: uppercase;
    border-radius: 4px;

    transition: background-color 0.2s linear;

    &:hover {
      background-color: $charcoal;
    }
  }
}

.title {
  margin-top: 20px;
  margin-left: 32px;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  font-family: 'Source Sans Pro', sans-serif;
  font-weight: 900;
  color: $pewter;
  text-transform: uppercase;
  font-size: 30px;
  line-height: 24px;
  letter-spacing: 0.8px;
  @media screen and (min-width: $break-lg) {
    font-size: 35px;
    line-height: 26px;
    letter-spacing: 0.8px;
    margin-top: 23px;
    margin-left: 37px;
  }
  @media screen and (min-width: $break-xl) {
    font-size: 40px;
    line-height: 28px;
    letter-spacing: 0.9px;
    margin-top: 26px;
    margin-left: 42px;
  }
  @media screen and (min-width: $break-xxl) {
    font-size: 45px;
    line-height: 30px;
    letter-spacing: 0.9px;
    margin-top: 29px;
    margin-left: 47px;
  }
}

.footnote {
  height: 44px;
  background: transparent linear-gradient(268deg, #C961C5 0%, #DEBFDD 100%) 0% 0% no-repeat padding-box;
  box-shadow: 0 3px 6px #00000029;
  opacity: 0.9;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  @media screen and (min-width: $break-lg) {
    height: 49px;
    span {
      font-size: 18px;
      line-height: 20px;
      margin-right: 50px;
    }
  }
  @media screen and (min-width: $break-xl) {
    height: 54px;
    span {
      font-size: 18px;
      line-height: 22px;
      margin-right: 55px;
    }
  }
  @media screen and (min-width: $break-xxl) {
    height: 59px;
    span {
      font-size: 20px;
      line-height: 25px;
      margin-right: 60px;
    }
  }

  span {
    font-family: 'Source Sans Pro', sans-serif;
    font-size: 16px;
    line-height: 18px;
    color: $charcoal;
    margin-right: 45px;
  }
}

.title svg {
  margin-right: 0.5em;
}
.container-fluid.first-view {
  padding: 99px 87px !important;
  background-image: url('../../assets/images/backgrounds/IRIS-background-720p.jpg');
  background-position: center;
  background-size: cover;
  display: flex;
  justify-content: center;
  align-items: center;
  .background-overlay {
    position: absolute;
    width: 100%;
    height: 100%;
    background-color: #2a274b;
    opacity: 0.4;
  }
}

.logo {
  position: fixed;
  bottom: 20px;
  right: 50px;
  width: 6.5%;
  height: auto;
}

.container-fluid {
  height: 100%;
}

.card-overflow {
  max-width: 1300px;
  max-height: 90vh;
  overflow-y: scroll;
  overflow-x: hidden;
}

.loader {
  border: 12px solid #f3f3f3;
  border-radius: 50%;
  border-top: 12px solid $blueberry;
  width: 60px;
  height: 60px;
  -webkit-animation: spin 2s linear infinite; /* Safari */
  animation: spin 2s linear infinite;
  margin: 0 auto;
}

@-webkit-keyframes spin {
  0% { -webkit-transform: rotate(0deg); }
  100% { -webkit-transform: rotate(360deg); }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

</style>
<style lang="scss">
@import "../../assets/scss/variables";

.select-group-item {
  margin: 5px;
  width: 30%;

  .el-input__inner {
    font-family: 'Helvetica', sans-serif;
    font-size: 13px;
    line-height: 20px;
    letter-spacing: 0;
    color: $default-focus;
    background-color: white !important;
    border: 2px solid #EBEDED !important;
    border-radius: 6px;
  }
}
</style>
